import { DbRef } from '@caresend/types';
import { getPath } from './path.js';
import { formatTimeZoneDateSimple, getTimeZoneLabel } from '../formatters/date.js';
import { SECOND, MINUTE } from '../initializers/date.js';

/**
 * ```
 * `${DbRef.WAYPOINTS}/${id}/${childPath}`
 * ```
 */

function getWaypointPath(id, childPath) {
  return getPath(`${DbRef.WAYPOINTS}/${id}`, childPath);
}
/**
 * ```
 * `${DbRef.WAYPOINT_ACTIONS}/${id}/${childPath}`
 * ```
 */

function getWaypointActionPath(id, childPath) {
  return getPath(`${DbRef.WAYPOINT_ACTIONS}/${id}`, childPath);
}
/**
 * Given a timestamp and an interval, rounds the timestamp minutes down to the nearest interval.
 *
 * Example:
 * 1678977149000 = Thursday, March 16, 2023 2:32:29 PM
 * 1678977000000 = Thursday, March 16, 2023 2:30:00 PM
 * roundDownMinutes(1678977149000, 10) => 1678977000000
 *
 * 1679013849000 = Friday, March 17, 2023 12:44:09 AM
 * 1679013000000 - Friday, March 17, 2023 12:30:00 AM
 * roundDownMinutes(1679013849000, 15) => 1679013000000
 */

const roundDownMinutes = (timestamp, interval = 15) => {
  const date = new Date(timestamp);
  const minutes = date.getMinutes();
  const remainder = minutes % interval;
  const roundedMinutes = minutes - remainder;
  date.setMinutes(roundedMinutes, 0, 0);
  return date.getTime();
};
/**
 * Given a start date object, returns the start and end time of the date
 * as well the formatted start and end time with the time zone.
 *
 * For the end time:
 * - If a date is passed, it will be returned formatted
 * - If x number is passed, x minutes will be added to the start time
 * - If nothing is passed, 15 minutes will be added to the start time
 */


const getNotificationTimeInfo = (start, end = 15) => {
  const {
    timestamp,
    timeZone: tz
  } = start;

  if (!timestamp) {
    throw Error('Missing start date timestamp for notification time info');
  }

  const startTimeMilliseconds = timestamp;
  let endTimeMilliseconds;

  if (typeof end === 'number') {
    endTimeMilliseconds = timestamp + end * MINUTE;
  } else if (!end.timestamp) {
    throw Error('Missing end date timestamp for notification time info');
  } else {
    endTimeMilliseconds = end.timestamp;
  }

  const formattedDateStart = formatTimeZoneDateSimple({
    timestamp: startTimeMilliseconds,
    timeZone: tz
  }, 'MM/DD/YYYY');
  const formattedStart = formatTimeZoneDateSimple({
    timestamp: startTimeMilliseconds,
    timeZone: tz
  }, 'h:mm a');
  const formattedEnd = formatTimeZoneDateSimple({
    timestamp: endTimeMilliseconds,
    timeZone: tz
  }, 'h:mm a');
  const timeZone = getTimeZoneLabel(tz);
  return {
    startTime: startTimeMilliseconds / SECOND,
    endTime: endTimeMilliseconds / SECOND,
    formattedDateStart,
    formattedStart,
    formattedEnd,
    timeZone
  };
};
/**
 * Given Waypoint.transitInfo, will calculate the estimated arrival time.
 */

const calculateEstimatedArrivalTime = transitInfo => {
  var _a, _b, _c;

  const departureTimestamp = (transitInfo === null || transitInfo === void 0 ? void 0 : transitInfo.delayInfo) ? transitInfo.delayInfo.timestamp : (_b = (_a = transitInfo === null || transitInfo === void 0 ? void 0 : transitInfo.departureTime) === null || _a === void 0 ? void 0 : _a.timestamp) !== null && _b !== void 0 ? _b : 0;
  const transitDuration = (transitInfo === null || transitInfo === void 0 ? void 0 : transitInfo.delayInfo) ? transitInfo.delayInfo.newDrivingEstimate : (_c = transitInfo === null || transitInfo === void 0 ? void 0 : transitInfo.estimatedTransitDuration) !== null && _c !== void 0 ? _c : 0;
  const departureTime = new Date(departureTimestamp);
  departureTime.setSeconds(departureTime.getSeconds() + transitDuration);
  return departureTime.getTime();
};
/** Given a waypoint date and transit info, will calculate the new timeInfo. */

const calculateNurseArrivalTimeInfo = (waypointDate, transitInfo, preciseTimeRange) => {
  const scheduledTime = waypointDate.timestamp;

  if (!scheduledTime) {
    throw Error('Missing scheduled time for nurse arrival time info');
  }

  const arrivalTime = calculateEstimatedArrivalTime(transitInfo);
  const isNurseArrivingEarly = scheduledTime > arrivalTime;
  const arrivalRangeStart = isNurseArrivingEarly ? scheduledTime : roundDownMinutes(arrivalTime, 10);
  const startDate = {
    timestamp: arrivalRangeStart,
    timeZone: waypointDate.timeZone
  };
  const timeRangeMinutes = preciseTimeRange ? 15 : 60;
  const nurseArrivalTimeInfo = getNotificationTimeInfo(startDate, timeRangeMinutes);
  return nurseArrivalTimeInfo;
};

export { calculateEstimatedArrivalTime, calculateNurseArrivalTimeInfo, getNotificationTimeInfo, getWaypointActionPath, getWaypointPath };
